import {MoveableOptions} from "react-moveable/types";
import {ArrayFormat, MoveableRefType, ResizableOptions, ScalableOptions} from "moveable";
import {deepCopy} from "./interactable-helpers";
import Sortable from "sortablejs";

export const FORBIDDEN_KEYS_TO_CLONE = ['props', 'ables', 'target'];
export const FADE_IN_DURATION = 100;
export const FADE_OUT_DURATION = 100;
export const DEFAULT_SNAP_THRESHOLD = 5;
export const TEXT_SNAP_THRESHOLD = 1;
export const SCALE_RESIZE_THRESHOLD = 5;
export const SCALE_RESIZE_THRESHOLD_SHAPE = 15;
export const LIGHT_CONTROLS_THRESHOLD = 40;
export const CONTROL_BOX_CLASS_PREFIX = 'original-elem-';
export const SELECTOR_ALL_COMPONENTS = 'div.page .content [data-component]';
export const ATTR_NAME = 'data-mstate';
export const LOCKED_ATTR_NAME = 'data-mstate-locked';
export const LOCKED_FRAME_ATTR_NAME = 'data-mstate-frame-locked';
export const START_FRAME = 'startFrame';
export const START_STYLE = 'startStyle';
export const START_PAGE = 'startPage';
export const START_DIRECTIONS = 'startDirections';

const DEFAULT_DIRECTIONS = ["nw", "ne", "w", "e", "sw", "se"];
const ALL_DIRECTIONS = ["nw", "ne", "n", "w", "e", "sw", "s", "se"];
const LEFT_RIGHT_DIRECTIONS = ["w", "e"];
const TOP_BOTTOM_DIRECTIONS = ["n", "s"];
export const TOP_BOTTOM_LEFT_RIGHT_DIRECTIONS = [...LEFT_RIGHT_DIRECTIONS, ...TOP_BOTTOM_DIRECTIONS];
const CORNER_DIRECTIONS = ["nw", "ne", "sw", "se"];

export interface InitialOptions extends MoveableOptions {
  selected: boolean,
  locked: boolean,
  favorite: boolean
}

export type AllowedElement = HTMLElement | SVGElement;

export type TargetType = MoveableRefType | ArrayFormat<MoveableRefType> | undefined;

export type ConfigPerComponentType = { [t in ComponentType]: MoveableOptions; };

export interface PageCoordinate {
  width: number,
  height: number,
  x: number,
  y: number
}

export interface Frame {
  translate: number[],
  scale: number[],
  rotate: number
}

export interface Undo {
  target: AllowedElement,
  oldFrame: Frame,
  newFrame: Frame,
  oldStyle: string,
  newStyle: string
}

export interface STRING_NUMBER_OBJECT {
  [key: string]: number
}

export enum InteractionType { DRAG, RESIZE, ROTATE, SCALE}

export enum Edge {
  LEFT = 'w',
  RIGHT = 'e',
  TOP = 'n',
  BOTTOM = 's',
  TOP_RIGHT = 'ne',
  BOTTOM_RIGHT = 'se',
  TOP_LEFT = 'nw',
  BOTTOM_LEFT = 'sw',
  NA = ''
}

export enum ComponentType { GENERIC, FRAME, FRAME_PREVIEW, ILLUSTRATION, TEXT, CURVED_TEXT, MENU_OBJECT, QR, IMAGE, SQUARE, CIRCLE, LINE, GROUP}

export enum AlignType { HORIZONTAL = 'horizontal', VERTICAL = 'vertical'}

export enum AlignDirection { START = 'start', CENTER = 'center', END = 'end'}

export const ALL_SNAPS_ENABLED = {"top": true, "right": true, "bottom": true, "left": true, "center": true, "middle": true};
export const LEFT_RIGHT_SNAPS_ENABLED = {"top": false, "right": true, "bottom": false, "left": true, "center": true, "middle": false};
export const TOP_BOTTOM_SNAPS_ENABLED = {"top": true, "right": false, "bottom": true, "left": false, "center": false, "middle": true};
export const ALL_SNAPS_DISABLED = {"top": false, "right": false, "bottom": false, "left": false, "center": false, "middle": false};

export const CORE_OPTIONS = {
  dragArea: false,
  useResizeObserver: false,
  padding: {"left": 0, "top": 0, "right": 0, "bottom": 0},
  keepRatio: false,
  throttleScale: 0,
  throttleDrag: 1,
  zoom: 1,
  origin: false,
  triggerAblesSimultaneously: true,
  pinchOutside: false,
  hideDefaultLines: true,
} as MoveableOptions;

export const ONLY_RESIZABLE_OPTIONS = {
  resizable: {
    edge: ALL_DIRECTIONS,
    renderDirections: ALL_DIRECTIONS,
  } as ResizableOptions
} as MoveableOptions;

export const RESIZABLE_OPTIONS = {
  resizable: {
    edge: LEFT_RIGHT_DIRECTIONS,
    renderDirections: LEFT_RIGHT_DIRECTIONS,
  } as ResizableOptions
} as MoveableOptions;

export const ROTATABLE_OPTIONS = {
  rotatable: true,
  rotationPosition: 'bottom'
} as MoveableOptions;

export const DRAGGABLE_OPTIONS = {
  draggable: true,
  edgeDraggable: TOP_BOTTOM_DIRECTIONS
} as MoveableOptions;

export const SCALABLE_OPTIONS = {
  scalable: {
    edge: [],
    renderDirections: CORNER_DIRECTIONS,
  } as ScalableOptions
} as MoveableOptions;

export const SNAPPABLE_OPTIONS = {
  snappable: true
}

export const GROUPABLE_OPTIONS = {
  groupable: true
}

const GENERIC_OPTIONS = {
  ...CORE_OPTIONS,
  ...RESIZABLE_OPTIONS,
  ...ROTATABLE_OPTIONS,
  ...DRAGGABLE_OPTIONS,
  ...SNAPPABLE_OPTIONS,
  //...GROUPABLE_OPTIONS
};

export function getConfig(type: ComponentType) {
  switch (type) {
    case ComponentType.GENERIC: return deepCopy(GENERIC_OPTIONS, true);
    case ComponentType.TEXT: return deepCopy({
      ...GENERIC_OPTIONS,
      ...SCALABLE_OPTIONS,
      checkInput: false,
      preventClickEventOnDrag: false,
      useResizeObserver: true
    } as MoveableOptions, true);
    case ComponentType.CURVED_TEXT: return deepCopy({
      ...GENERIC_OPTIONS,
      ...SCALABLE_OPTIONS,
      resizable: false,
      checkInput: false,
      preventClickEventOnDrag: false,
      useResizeObserver: true
    } as MoveableOptions, true);
    case ComponentType.MENU_OBJECT: return deepCopy({
      ...GENERIC_OPTIONS,
      ...SCALABLE_OPTIONS,
      useResizeObserver: true
    }, true);
    case ComponentType.FRAME_PREVIEW: return deepCopy({
      origin: false,
      rotatable: false,
      resizable: false,
      scalable: false,
      rotationPosition: 'none',
      triggerAblesSimultaneously: false
    }, true);
    case ComponentType.SQUARE: return deepCopy({
      ...GENERIC_OPTIONS,
      edgeDraggable: false,
      ...ONLY_RESIZABLE_OPTIONS
    }, true);
    case ComponentType.CIRCLE: return deepCopy({
      ...GENERIC_OPTIONS,
      edgeDraggable: false,
      ...ONLY_RESIZABLE_OPTIONS
    }, true);
    case ComponentType.LINE: return deepCopy({
      ...GENERIC_OPTIONS,
      resizable: {
        edge: LEFT_RIGHT_DIRECTIONS,
        renderDirections: LEFT_RIGHT_DIRECTIONS,
      } as ResizableOptions
    }, true);
    case ComponentType.FRAME: return deepCopy({
      ...GENERIC_OPTIONS,
      edgeDraggable: false,
      ...ONLY_RESIZABLE_OPTIONS
    }, true);
    case ComponentType.QR: return deepCopy({
      ...CORE_OPTIONS,
      ...ROTATABLE_OPTIONS,
      ...DRAGGABLE_OPTIONS,
      ...SCALABLE_OPTIONS,
      ...SNAPPABLE_OPTIONS,
      keepRatio: true
    }, true);
    case ComponentType.IMAGE: return deepCopy({
      ...GENERIC_OPTIONS,
      edgeDraggable: false,
      ...ONLY_RESIZABLE_OPTIONS
    }, true);
    case ComponentType.ILLUSTRATION: return deepCopy({
      ...GENERIC_OPTIONS,
      edgeDraggable: false,
      ...ONLY_RESIZABLE_OPTIONS
    }, true);
    case ComponentType.GROUP: return deepCopy({
      ...CORE_OPTIONS,
      ...DRAGGABLE_OPTIONS,
      rotationPosition: 'none',
      checkInput: true
    }, true);
    default: return {};
  }
}

export const ABLE_OFFSET = {
  ANGLE: 50,
  MOOPTIONS: -60,
  OPTIONS: -60,
  DIM: 50,
  LOCKED: 20,
  FAVORITE: -40,
  reset: () => {
    ABLE_OFFSET.ANGLE = 50;
    ABLE_OFFSET.MOOPTIONS = -60;
    ABLE_OFFSET.OPTIONS = -60;
    ABLE_OFFSET.DIM = 50;
    ABLE_OFFSET.LOCKED = 20;
    ABLE_OFFSET.FAVORITE = -40;
  }
}

export const SORTABLE_OPTIONS = {
  handle: '.item',
  animation: 150,
  direction: 'vertical',
  swapThreshold: 0.50,
} as Sortable.Options;
